import { type FC, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Button,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import { useTranslation } from "@/shared/hooks";
import {
  ASSISTANT_Z_INDEX,
  FixWithAssistantButton,
  useAssistantPanelOpen,
} from "@/features/assistant";
import { lastTouchInput } from "@/shared/utils/attribution";
import { formatCurrency } from "@/shared/utils";
import { setPaymentIdParam } from "@/shared/utils/paymentIdParam";
import {
  CreatePaymentMutation,
  CreateProviderPaymentMutation,
  StripePublishableKeyQuery,
} from "@/entities/payments";
import {
  type CreatePaymentMutationType,
  type CreatePaymentMutationVariablesType,
  type CreateProviderPaymentMutationType,
  type CreateProviderPaymentMutationVariablesType,
  type StripePublishableKeyQueryType,
} from "@/types/graphql/site.generated";
import { StripeCheckoutStep } from "./StripeCheckoutStep";
import { PaymentStatusStep } from "./PaymentStatusStep";
import { PayPalCheckoutButton } from "./PayPalCheckoutButton";
import type { TerminalStatus } from "./terminalStatus";
import { salesClosedMessageKey } from "./salesClosed";
import { checkoutLayout } from "./checkoutLayout";

interface PaymentDialogPriceProvider {
  name: string;
  clientId?: string | null;
}

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  priceToken: string;
  recurrent: boolean;
  /** Способы оплаты активной цены (спека 2026-09-17-paypal-ppv §5.1):
   * порядок значимый, первый — основная форма, PAYPAL в списке — кнопка под
   * ней (или вместо формы, если основных способов нет). */
  providers: PaymentDialogPriceProvider[];
  /** Валюта активной цены — нужна PayPalScriptProvider до того, как заведён
   * платёж (спека §6). */
  currency: string;
  /** Что и почём покупают: показывается в раскрытии условий подписки и на
   * кнопке оплаты (референс PaymentForm.js:57-131). */
  productTitle: string;
  amountLabel: string;
  priceOnPeriod: string | null;
  onPromocodeInvalid?: () => void;
  /** Платёж подтверждён (поллинг вернул SUCCEEDED): родитель перезапрашивает
   * доступ к контенту/продукту. */
  onSucceeded?: () => void;
}

type Step = "creating" | "checkout" | "polling" | "error";

// Marker type for internal guard-clause failures (missing payment id /
// client secret) — never surfaced verbatim to the user, see Finding 3.
class PaymentSetupError extends Error {}

type ProviderPaymentResult =
  | { clientSecret: string }
  // Paynet: form-post со своими полями. CCBill: обычный GET-редирект,
  // fields === null (см. submitPaynetRedirectForm и ветку ниже).
  | { redirectUrl: string; fields: Record<string, string> | null };

// Paynet (Task 6/10) принимает оплату только через form-post, GET-редирект
// на redirectUrl не работает. `fields` приходит от бэкенда уже готовым по
// вендорской документации — поля перебираются как есть, без переименования
// и фильтрации (там есть, например, "LinkUrlSucces" с одной "s" — опечатка
// самого Paynet, воспроизводимая дословно).
function submitPaynetRedirectForm(
  redirectUrl: string,
  fields: Record<string, string>,
): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = redirectUrl;
  form.style.display = "none";
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

// MUI Dialog defaults to theme.zIndex.modal (1300), above ASSISTANT_Z_INDEX
// (1250): FixWithAssistantButton below opens the panel on top of this
// dialog without closing it, so at the default z-index the panel would
// render invisible/unclickable underneath. Stays above Header (1200) and
// RouteDialog (1199, this dialog can open on top of the product route
// dialog), below the assistant panel.
const DIALOG_Z_INDEX = ASSISTANT_Z_INDEX - 30;

export const PaymentDialog: FC<PaymentDialogProps> = ({
  open,
  onClose,
  productId,
  priceToken,
  recurrent,
  providers,
  currency,
  productTitle,
  amountLabel,
  priceOnPeriod,
  onPromocodeInvalid,
  onSucceeded,
}) => {
  const { t } = useTranslation();
  const {
    hasPaypalProvider,
    skipPrimaryAttempt,
    primaryByButton,
    paypalVisible,
  } = checkoutLayout(providers);
  const theme = useTheme();
  // Референс ProductDialog.js:58-67: на телефоне диалог во весь экран,
  // скролл страницы, и закрыть его можно только крестиком (ни клик по фону,
  // ни Esc не должны прерывать оплату).
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // FixWithAssistantButton ниже открывает панель помощника поверх этого
  // диалога, не закрывая его. Пока панель открыта, снимаем focus trap:
  // иначе Dialog силой возвращает фокус себе при попытке напечатать в поле
  // помощника, которое живёт вне его DOM-поддерева.
  const assistantPanelOpen = useAssistantPanelOpen();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const [step, setStep] = useState<Step>("creating");
  // Покупатель нажал «Оплатить картой» (только при primaryByButton).
  const [primaryRequested, setPrimaryRequested] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  // Сумма первого списания приходит от сервера (триал, промокод, комиссии):
  // цена из карточки для платного триала «7 дней за $1» показала бы на
  // кнопке полную стоимость подписки.
  const [chargeLabel, setChargeLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Terminal status reported back by PaymentStatusStep once polling settles,
  // so the dialog title can stop claiming "pending" (Minor #3).
  const [pollingStatus, setPollingStatus] = useState<TerminalStatus | null>(
    null,
  );

  // Закрытие диалога — единственная точка, где paymentId уходит из адреса:
  // эффект сброса состояния ниже срабатывает на !open уже после того, как
  // родитель размонтировал/скрыл диалог, и не отличает закрытие покупателем
  // от размонтирования всей страницы.
  const handleClose = () => {
    setPaymentIdParam(null);
    onClose();
  };

  const {
    data: keyData,
    loading: keyLoading,
    error: keyError,
  } = useQuery<StripePublishableKeyQueryType>(StripePublishableKeyQuery, {
    skip: !open,
  });
  // Ключа Stripe нет (запрос упал или площадка не настроена): без этой
  // ветки шаг чекаута висел бы вечным спиннером.
  const keyMissing =
    !keyLoading && (!!keyError || (!!keyData && !keyData.stripePublishableKey));

  // Параметры покупки живут в ref, а не в зависимостях эффекта ниже:
  // Product.prices[].token пересобирается и переподписывается бэкендом на
  // КАЖДЫЙ запрос продукта, поэтому рефетч после успешной оплаты менял
  // priceToken, эффект перезапускался и заводил ещё один PENDING-платёж,
  // показывая покупателю форму оплаты поверх уже прошедшей (прод,
  // 2026-09-11). Цепочка создания платежа должна отрабатывать ровно один
  // раз на одно открытие диалога.
  const paymentParamsRef = useRef({
    productId,
    priceToken,
    recurrent,
    skipPrimaryAttempt,
    primaryByButton,
  });
  useEffect(() => {
    paymentParamsRef.current = {
      productId,
      priceToken,
      recurrent,
      skipPrimaryAttempt,
      primaryByButton,
    };
  }, [productId, priceToken, recurrent, skipPrimaryAttempt, primaryByButton]);
  // Платёж для текущего открытия уже заводился: повторно не создаём ни при
  // каком ререндере (шаг checkout/polling, пришедший viewerPurchase и т.п.).
  const createStartedRef = useRef(false);

  const [createPayment] = useMutation<
    CreatePaymentMutationType,
    CreatePaymentMutationVariablesType
  >(CreatePaymentMutation);
  const [createProviderPayment] = useMutation<
    CreateProviderPaymentMutationType,
    CreateProviderPaymentMutationVariablesType
  >(CreateProviderPaymentMutation);

  useEffect(() => {
    if (!open) {
      createStartedRef.current = false;
      setStep("creating");
      setPaymentId(null);
      setClientSecret(null);
      setChargeLabel(null);
      setError(null);
      setPollingStatus(null);
      setPrimaryRequested(false);
      return;
    }

    if (createStartedRef.current) return;
    // Редиректный способ (Paynet) стартует по кнопке «Оплатить картой».
    if (paymentParamsRef.current.primaryByButton && !primaryRequested) return;
    // Единственный подключённый способ — PayPal: платёж под основного
    // провайдера не заводим вовсе (спека §6, PaymentDialog.tsx). Кнопка
    // PayPal ниже заводит собственный платёж по клику.
    if (paymentParamsRef.current.skipPrimaryAttempt) return;
    createStartedRef.current = true;

    // Guards against a stale create-chain (from a closed/reopened dialog)
    // clobbering the new attempt's state after it resolves late (Task 17
    // review, Finding 2).
    let cancelled = false;
    // Цепочка досчиталась (успех, редирект или ошибка): повторять её на
    // новом прогоне эффекта нельзя, платёж уже заведён.
    let settled = false;

    (async () => {
      try {
        // priceToken is the server-signed token from Product.prices[].token
        // (Task 2) — it is passed through to createPayment unmodified. The
        // client never computes or sends a raw price/amount value.
        const { productId, priceToken, recurrent } = paymentParamsRef.current;
        const { data: paymentData } = await createPayment({
          variables: {
            productId,
            priceToken,
            recurrent,
            utm: lastTouchInput(),
          },
        });
        // Мутация прошла: платёж на сервере уже есть, второй раз эту цепочку
        // запускать нельзя ни при каком прогоне эффекта.
        settled = true;
        if (cancelled) return;
        const created = paymentData?.createPayment;
        const id = created?.id;
        if (!id) throw new PaymentSetupError();
        setPaymentId(id);
        if (created.amount) {
          setChargeLabel(
            formatCurrency(created.amount.value, created.amount.currency),
          );
        }
        // paymentId в адресе (спека §4.2): перезагрузка страницы посреди
        // оплаты и возврат с редиректных провайдеров возвращают покупателя
        // к опросу статуса (ProductScreen → PaymentStatusDialog).
        setPaymentIdParam(id);

        // 100%-скидка (бесплатный PPV): backend вернул платёж сразу
        // SUCCEEDED — Stripe-шаг пропускается, поллинг подтвердит успех.
        if (created.status === "SUCCEEDED") {
          setStep("polling");
          return;
        }

        const { data: providerData } = await createProviderPayment({
          variables: { id },
        });
        if (cancelled) return;
        const result = providerData?.createProviderPayment as
          | ProviderPaymentResult
          | undefined;

        if (result && "redirectUrl" in result) {
          // Уводим покупателя на страницу провайдера — сама страница сейчас
          // навигирует прочь, дальнейшее состояние диалога уже не важно.
          //
          // Paynet принимает оплату только form-post'ом (fields — объект).
          // CCBill (FlexForms) отдаёт уже готовый адрес: все параметры,
          // включая formDigest, зашиты в query string, тела формы нет и
          // отправлять нечего, поэтому fields === null и это обычный
          // GET-переход.
          if (result.fields) {
            submitPaynetRedirectForm(result.redirectUrl, result.fields);
          } else {
            window.location.href = result.redirectUrl;
          }
          return;
        }

        const secret =
          result && "clientSecret" in result ? result.clientSecret : undefined;
        if (!secret) throw new PaymentSetupError();
        setClientSecret(secret);
        setStep("checkout");
      } catch (e) {
        settled = true;
        if (cancelled) return;
        // Promocode invalidated between apply and checkout (limit reached
        // concurrently, or the trial was already used) — surface a snackbar,
        // drop the now-invalid promocode from ProductScreen (triggers a
        // ProductQuery refetch without it) and close the dialog instead of
        // showing the generic error step (Task 9).
        const cause =
          e instanceof ApolloError
            ? e.graphQLErrors?.[0]?.extensions?.cause
            : undefined;
        // Продукт уже куплен (гонка двух вкладок): это не ошибка оплаты,
        // доступ есть — обновляем продукт и закрываем диалог, как легаси
        // (CreatePayment.js:86-89). Отдельного кода ошибки у бэкенда пока
        // нет, поэтому ловим и cause промокодов, и текст сообщения.
        const alreadyPurchased =
          cause === "PRODUCT_ALREADY_PURCHASED" ||
          (e instanceof Error && /already purchased/i.test(e.message));
        if (alreadyPurchased) {
          onSucceeded?.();
          handleClose();
          return;
        }
        // Подписка зрителя на этот продукт просрочена (ревью I1): вместо
        // второй подписки замена карты существующей, она продлится сразу.
        if (cause === "SUBSCRIPTION_PAST_DUE") {
          const pastDueId =
            e instanceof ApolloError
              ? e.graphQLErrors?.[0]?.extensions?.subscriptionId
              : undefined;
          enqueueSnackbar(t("purchases.pastDue.paywall"), {
            variant: "warning",
          });
          handleClose();
          void router.push(
            typeof pastDueId === "string"
              ? `/purchases?updateCard=${encodeURIComponent(pastDueId)}`
              : "/purchases",
          );
          return;
        }
        // Продажи закрылись, пока страница была открыта (аудит денег
        // 2026-10, I2): переведённый текст вместо русского сообщения
        // бэкенда, продукт обновляется, диалог закрывается.
        const salesClosedKey = salesClosedMessageKey(cause);
        if (salesClosedKey) {
          enqueueSnackbar(t(salesClosedKey), { variant: "warning" });
          onSucceeded?.();
          handleClose();
          return;
        }
        if (
          cause === "PROMOCODE_LIMIT_REACHED" ||
          cause === "TRIAL_NOT_AVAILABLE"
        ) {
          enqueueSnackbar(
            t(
              cause === "TRIAL_NOT_AVAILABLE"
                ? "payment.trialNotAvailableError"
                : "payment.promocodeLimitReachedError",
            ),
            { variant: "error" },
          );
          onPromocodeInvalid?.();
          handleClose();
          return;
        }
        // Internal guard-clause failures (missing id/secret) never carry a
        // user-facing message — always show the translated generic error.
        // Only genuinely user-facing errors (e.g. Stripe's own messages)
        // should fall through to e.message (Task 17 review, Finding 3).
        setError(
          e instanceof Error && !(e instanceof PaymentSetupError)
            ? e.message
            : t("payment.errorTitle"),
        );
        setStep("error");
      }
    })();

    return () => {
      cancelled = true;
      // StrictMode (и любой повторный прогон эффекта) обрывает цепочку до
      // ответа сервера: тогда платёж не заведён и следующему прогону нужно
      // разрешить начать заново.
      if (!settled) createStartedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, primaryRequested]);

  // Reflect the actual outcome instead of always claiming "pending"
  // (Minor #3): the "creating"/"checkout" steps are still in progress, but
  // once polling settles, mirror PaymentStatusStep's own title.
  const dialogTitle = (() => {
    // Ключа Stripe нет, но PayPal есть (спека §6): форма чекаута просто не
    // рисуется, это не ошибка — заголовок остаётся обычным "Оплата".
    if (
      step === "error" ||
      (step === "checkout" && keyMissing && !hasPaypalProvider)
    ) {
      return t("payment.errorTitle");
    }
    if (step === "creating" || step === "checkout") {
      return t("payment.checkoutTitle");
    }
    if (step === "polling") {
      if (pollingStatus === "SUCCEEDED") return t("payment.succeededTitle");
      // REFUNDED (полностью возвращённый платёж) показывается как отмена:
      // отдельного текста про возврат в переводах витрины нет.
      if (pollingStatus === "CANCELED" || pollingStatus === "REFUNDED") {
        return t("payment.canceledTitle");
      }
      if (pollingStatus === "ERROR") return t("payment.errorTitle");
    }
    return t("payment.pendingTitle");
  })();

  // Пока платежа нет, на кнопке цена из карточки; как только сервер вернул
  // платёж, показываем ровно ту сумму, которую спишут.
  const payAmountLabel = chargeLabel ?? amountLabel;
  // Первое списание отличается от цены продления (платный триал, промокод):
  // легаси в этом случае раскрывал обе суммы (PaymentForm.js:57-131).
  const firstChargeLabel =
    chargeLabel && chargeLabel !== amountLabel ? chargeLabel : null;

  const isCheckoutReady =
    step === "checkout" && clientSecret && !!keyData?.stripePublishableKey;
  const isCheckoutLoading =
    step === "checkout" && !keyMissing && !keyData?.stripePublishableKey;

  // Кнопка «Оплатить картой» ждёт клика; после клика идёт заведение
  // платежа и редирект, PayPal в это время прячем.
  const waitingForCardButton =
    primaryByButton && !primaryRequested && step === "creating";
  const redirectingToPrimary =
    primaryByButton && primaryRequested && step === "creating";
  // «или» между основным способом и PayPal: только когда над PayPal
  // действительно что-то есть (кнопка картой или форма Stripe).
  const showPaypalDivider =
    paypalVisible && (waitingForCardButton || !!isCheckoutReady);

  // Платёж подтверждён PayPal и захвачен на сервере (спека §6): диалог
  // переключается на опрос статуса тем же paymentId, что и у Stripe/Paynet.
  const handlePayPalApproved = (approvedPaymentId: string) => {
    setPaymentId(approvedPaymentId);
    setPaymentIdParam(approvedPaymentId);
    setStep("polling");
  };

  return (
    <Dialog
      open={open}
      scroll="body"
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus={assistantPanelOpen}
      sx={{ zIndex: DIALOG_Z_INDEX }}
    >
      <DialogTitle>
        {dialogTitle}
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* skipPrimaryAttempt: платёж под основного провайдера не заводили
          вовсе (единственный подключённый способ — PayPal), шаг чекаута
          остаётся "creating" насовсем — спиннер здесь только сбивал бы с
          толку, кнопка PayPal ниже и есть форма оплаты. */}
        {(step === "creating" || isCheckoutLoading) &&
          !skipPrimaryAttempt &&
          !waitingForCardButton && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
        {waitingForCardButton && (
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => setPrimaryRequested(true)}
          >
            {t("payment.payByCard", { amount: payAmountLabel })}
          </Button>
        )}
        {step === "error" && (
          <Box sx={{ textAlign: "center" }}>
            <Alert severity="error" sx={{ textAlign: "left" }}>
              {error}
            </Alert>
            {/* Платёж мог и не завестись (createPayment упал): тогда
              paymentId нет, и помощник проверяет продукт (GetProduct), не
              выключена ли у него оплата. Диалог открывают не только на
              /product/<id>, поэтому продукт передаём явно. */}
            <FixWithAssistantButton
              errorCode="PAYMENT_FAILED"
              productId={productId}
              paymentId={paymentId}
            />
          </Box>
        )}
        {/* Ключа Stripe нет, но PayPal есть — тихо уступаем место кнопке
          снизу вместо ошибки вслепую (спека §6): keyMissing срабатывает,
          только если способов оплаты не осталось вовсе. */}
        {step === "checkout" && keyMissing && !hasPaypalProvider && (
          <Alert severity="error">{t("payment.errorTitle")}</Alert>
        )}
        {isCheckoutReady && keyData?.stripePublishableKey && (
          <StripeCheckoutStep
            publishableKey={keyData.stripePublishableKey}
            clientSecret={clientSecret}
            onConfirmed={() => setStep("polling")}
            productTitle={productTitle}
            amountLabel={payAmountLabel}
            firstChargeLabel={firstChargeLabel}
            priceOnPeriod={priceOnPeriod}
            recurrent={recurrent}
          />
        )}
        {/* Кнопка PayPal под основной формой (спека §6): рисуется, только
          пока платёж ещё не подтверждён — на "polling" его заменяет экран
          опроса статуса. Сама решает, рисовать ли себя, по наличию PAYPAL
          в providers с непустым clientId. */}
        {showPaypalDivider && (
          <Divider sx={{ my: 2 }}>{t("payment.orDivider")}</Divider>
        )}
        {step !== "polling" && !redirectingToPrimary && (
          <PayPalCheckoutButton
            providers={providers}
            currency={currency}
            productId={productId}
            priceToken={priceToken}
            recurrent={recurrent}
            onApproved={handlePayPalApproved}
          />
        )}
        {step === "polling" && paymentId && (
          <PaymentStatusStep
            paymentId={paymentId}
            onClose={handleClose}
            onStatusChange={(status) => {
              setPollingStatus(status);
              if (status === "SUCCEEDED") {
                // Платёж завершён — возобновлять нечего, параметр из адреса
                // убирается сразу, не дожидаясь кнопки «Закрыть»: рефетч
                // ниже переключает карточку на «куплено» и размонтирует
                // этот диалог вместе с его handleClose.
                setPaymentIdParam(null);
                onSucceeded?.();
              }
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
